import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { STORAGE_KEY } from "@/lib/auth/constants";
import {
  cleanTokenData,
  isTokenExpired,
  loadTokenData,
} from "@/lib/auth/helpers";
import type { TokenData } from "@/types/auth";

export interface AuthContextValue {
  tokenData: TokenData | null;
  user: { email: string; name: string } | null;
  isAuthenticated: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  tokenData: null,
  user: null,
  isAuthenticated: false,
  logout: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const data = loadTokenData();
    if (data && isTokenExpired(data)) {
      cleanTokenData();
      setTokenData(null);
    } else {
      setTokenData(data);
    }
    setReady(true);
  }, []);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY.TOKEN_DATA) {
        const data = loadTokenData();
        setTokenData(data && !isTokenExpired(data) ? data : null);
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const logout = useCallback(() => {
    cleanTokenData();
    setTokenData(null);
    window.location.href = "/logout";
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      tokenData,
      user: tokenData
        ? {
            email: tokenData.claims.usr.email ?? "",
            name: tokenData.claims.usr.username,
          }
        : null,
      isAuthenticated: !!tokenData,
      logout,
    }),
    [tokenData, logout],
  );

  if (!ready) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
